import { useState } from "react";
import { Box, Typography } from "@mui/material";
import UnsavedChangesDialog from "./shared/UnsavedChangesDialog";

import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import ContentCutIcon from "@mui/icons-material/ContentCut";
import DashboardIcon from "@mui/icons-material/Dashboard";
import GroupsIcon from "@mui/icons-material/Groups";
import ScheduleIcon from "@mui/icons-material/Schedule";
import SettingsIcon from "@mui/icons-material/Settings";
import AccountBalanceWalletOutlinedIcon from "@mui/icons-material/AccountBalanceWalletOutlined";
import AddCircleOutlineOutlinedIcon from "@mui/icons-material/AddCircleOutlineOutlined";
import BarChartIcon from "@mui/icons-material/BarChart";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import EventBusyIcon from "@mui/icons-material/EventBusy";

import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useDashContext } from "../context/DashboardContext";

const LABEL_TO_SLUG = {
  Overview: "overview",
  Appointments: "appointments",
  Services: "services",
  "Shop Services": "services",
  Schedule: "schedule",
  "Shop Hours": "schedule",
  Barbers: "barbers",
  Clients: "clients",
  Exceptions: "exceptions",
  Analytics: "analytics",
  Settings: "settings",
  "Book Appointment": "book",
  Profile: "profile",
  Payments: "payments",
};

// ── Nav config per role ───────────────────────────────────────────────────────
const NAV_CONFIG = {
  owner: {
    tabs: [
      { label: "Overview", icon: <DashboardIcon sx={{ mr: 1 }} /> },
      { label: "Appointments", icon: <CalendarMonthIcon sx={{ mr: 1 }} /> },
      { label: "Shop Hours", icon: <ScheduleIcon sx={{ mr: 1 }} /> },
      { label: "Shop Services", icon: <ContentCutIcon sx={{ mr: 1 }} /> },
      { label: "Barbers", icon: <AccountCircleIcon sx={{ mr: 1 }} /> },
      { label: "Clients", icon: <GroupsIcon sx={{ mr: 1 }} /> },
    ],
    bottomTabs: [
      { label: "Analytics", icon: <BarChartIcon sx={{ mr: 1 }} /> },
      { label: "Settings", icon: <SettingsIcon sx={{ mr: 1 }} /> },
    ],
  },
  admin: {
    tabs: [
      { label: "Overview", icon: <DashboardIcon sx={{ mr: 1 }} /> },
      { label: "Appointments", icon: <CalendarMonthIcon sx={{ mr: 1 }} /> },
      { label: "Shop Hours", icon: <ScheduleIcon sx={{ mr: 1 }} /> },
      { label: "Shop Services", icon: <ContentCutIcon sx={{ mr: 1 }} /> },
      { label: "Barbers", icon: <AccountCircleIcon sx={{ mr: 1 }} /> },
      { label: "Clients", icon: <GroupsIcon sx={{ mr: 1 }} /> },
    ],
    bottomTabs: [
      { label: "Analytics", icon: <BarChartIcon sx={{ mr: 1 }} /> },
      { label: "Settings", icon: <SettingsIcon sx={{ mr: 1 }} /> },
    ],
  },
  barber: {
    tabs: [
      { label: "Overview", icon: <DashboardIcon sx={{ mr: 1 }} /> },
      { label: "Appointments", icon: <CalendarMonthIcon sx={{ mr: 1 }} /> },
      { label: "Schedule", icon: <ScheduleIcon sx={{ mr: 1 }} /> },
      { label: "Services", icon: <ContentCutIcon sx={{ mr: 1 }} /> },
      { label: "Exceptions", icon: <EventBusyIcon sx={{ mr: 1 }} /> },
      { label: "Clients", icon: <GroupsIcon sx={{ mr: 1 }} /> },
    ],
    bottomTabs: [
      { label: "Analytics", icon: <BarChartIcon sx={{ mr: 1 }} /> },
      {
        label: "Payments",
        icon: <AccountBalanceWalletOutlinedIcon sx={{ mr: 1 }} />,
      },
    ],
  },
  client: {
    tabs: [
      { label: "Overview", icon: <DashboardIcon sx={{ mr: 1 }} /> },
      { label: "Appointments", icon: <CalendarMonthIcon sx={{ mr: 1 }} /> },
      {
        label: "Book Appointment",
        icon: <AddCircleOutlineOutlinedIcon sx={{ mr: 1 }} />,
      },
    ],
    bottomTabs: [],
  },
};

// ── SideNavigation ────────────────────────────────────────────────────────────
const SideNavigation = ({ onNavigate } = {}) => {
  const { user } = useAuth();
  const { hasUnsavedChanges, setHasUnsavedChanges } = useDashContext();
  const navigate = useNavigate();
  const location = useLocation();
  const [pendingSlug, setPendingSlug] = useState(null);

  const role = user?.role;
  const config = NAV_CONFIG[role] || NAV_CONFIG.admin;

  // Derive active tab from URL so the highlight always reflects the current route.
  // Compare by slug (not label) since roles can use different labels for the same slug.
  const currentSlug = location.pathname.split("/")[2] || "overview";
  const isActive = (label) =>
    (LABEL_TO_SLUG[label] || label.toLowerCase()) === currentSlug;

  // The dashboard's URL effect sets the active tab from the slug, so
  // navigating is all that's needed here.
  const goTo = (slug) => {
    navigate(`/dashboard/${slug}`);
    onNavigate?.();
  };

  const renderTab = ({ label, icon }) => (
    <Box key={label} sx={{ width: "100%", display: "flex" }}>
      <Box
        onClick={() => {
          const slug = LABEL_TO_SLUG[label] || label.toLowerCase();
          if (slug === currentSlug) {
            onNavigate?.(); // already here — just close the mobile drawer
            return;
          }
          if (hasUnsavedChanges) {
            setPendingSlug(slug);
            return;
          }
          goTo(slug);
        }}
        sx={{
          display: "flex",
          alignItems: "center",
          width: "100%",
          padding: 1,
          borderRadius: 1,
          cursor: "pointer",
          backgroundColor: isActive(label) ? "background.default" : "transparent",
          color: isActive(label) ? "text.primary" : "text.secondary",
          "&:hover": {
            backgroundColor: "background.default",
            color: "text.primary",
          },
        }}
      >
        {icon}
        <Typography
          variant="body1"
          sx={{
            width: "100%",
            fontWeight: 400,
            display: "flex",
            alignItems: "center",
            color: "inherit",
          }}
        >
          {label}
        </Typography>
      </Box>
    </Box>
  );

  return (
    <Box
      sx={{
        width: "100%",
        borderRight: "0.1px solid",
        borderColor: "customDivider",
        height: { xs: "100vh", md: "calc(100vh - 64px)" },
        minHeight: "500px",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        backgroundColor: "offWhite",
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "space-between",
          height: "100%",
          width: "100%",
          p: 2,
        }}
      >
        {/* Main tabs */}
        <Box
          sx={{
            width: "100%",
            display: "flex",
            flexDirection: "column",
            gap: 1,
          }}
        >
          {config.tabs.map(renderTab)}
        </Box>

        {/* Bottom tabs (e.g. Settings) */}
        <Box
          sx={{
            width: "100%",
            display: "flex",
            flexDirection: "column",
            gap: 1,
          }}
        >
          {config.bottomTabs.map(renderTab)}
        </Box>
      </Box>

      {/* Footer */}
      <Box
        sx={{
          p: 2,
          borderTop: "0.1px solid",
          borderColor: "customDivider",
          display: "flex",
          justifyContent: "center",
        }}
      >
        <Typography
          variant="body2"
          sx={{ fontWeight: 400, color: "text.secondary" }}
        >
          &copy; 2026 Fadepilot.
        </Typography>
      </Box>

      {/* Unsaved-changes guard */}
      <UnsavedChangesDialog
        open={!!pendingSlug}
        onStay={() => setPendingSlug(null)}
        onDiscard={() => {
          const slug = pendingSlug;
          setPendingSlug(null);
          setHasUnsavedChanges(false);
          goTo(slug);
        }}
      />
    </Box>
  );
};

export default SideNavigation;
